import { useTranslation } from "react-i18next";

import styles from "./styles.module.scss";
import { useState, useCallback, useEffect, useRef } from "react";

import { AuthConfig } from "../../../setup/context/config/authConfig";
import { ROUTES } from "../../../setup/constants";
import { useNavigate } from "react-router-dom";
import { getExternalContracts } from "../../../setup/api/services/contract";
import ContractsListItem from "../contractListItem";
import {
  EContractType,
  IContractDetailsResponseData,
} from "../../../setup/api/services/contract/models";
import Spinner from "../../../components/common/Spinner";
import Pagination from "../pagination";
import PrimaryButton from "../../../components/common/PrimaryButton";
import SearchInput from "../../../components/common/searchInput";
import { useOrganizationCode } from "../../../setup/util/extractOrganizationCode";

const Contracts = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userToken = AuthConfig.getToken();
  const authorities = AuthConfig.getUserInfo()?.authorities;
  const [loading, setLoading] = useState(false);
  const [contracts, setContracts] = useState<IContractDetailsResponseData[]>(
    []
  );
  const [tabType, setTabType] = useState("active");

  const [page, setPage] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [totalCount, setTotalCount] = useState(1);
  const [activeTabPage, setActiveTabPage] = useState(1);
  const [prevTabPage, setPrevTabPage] = useState(1);
  const pageSize = 10;
  const [searchValue, setSearchValue] = useState("");

  const organizationCode = useOrganizationCode(userToken) || undefined;

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setTabType(newValue);
    if (newValue === "previous") {
      setPage(prevTabPage != 1 ? prevTabPage : 1);
    } else {
      setPage(activeTabPage != 1 ? activeTabPage : 1);
    }
  };

  const handlePaginationChange = (value: number) => {
    setPage(value);
    if (tabType === "active") {
      setActiveTabPage(value);
    } else {
      setPrevTabPage(value);
    }
  };

  const fetchContracts = useCallback(
    async (listType, contractId) => {
      setLoading(true);
      try {
        const body = {
          listType: `profile_${listType}`,
          contractId: contractId, // TODO: How to handle search by contract title?
          page: page - 1,
          size: pageSize,
          organizationCode: organizationCode,
          type: EContractType.COMPLEX_CONTRACT,
        };
        const response = await getExternalContracts(body);
        setContracts(response.data.content);
        setPagesCount(response.data.totalPages);
        setTotalCount(response.data.totalElements);
        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    },
    [organizationCode, page]
  );

  // This is the simplest possible solution - one useEffect with one strict flag
  const didMount = useRef(false);

  // Single useEffect with complete dependencies - but controls when it actually runs
  useEffect(() => {
    // Skip if the organizationCode isn't ready yet
    if (organizationCode === undefined) {
      return;
    }

    // Two conditions to run the fetch:
    // 1. First time (when didMount.current is false) - this runs exactly once
    // 2. After first mount when dependencies change - this handles user interactions
    if (!didMount.current) {
      // First-time case - mark it as mounted to ensure it only runs once
      fetchContracts(tabType, searchValue);
      didMount.current = true;
    } else {
      // Subsequent runs - only happens for actual dependency changes
      fetchContracts(tabType, searchValue);
    }
  }, [fetchContracts, organizationCode, page, tabType, searchValue]);

  return (
    <div className={styles.container}>
      <h1 className={styles.topHeader}>{t("lbl_contracts_list")}</h1>
      <div className={styles.topBar}>
        <div className={styles.tabsGroup}>
          <p
            onClick={(e) => handleTabChange(e, "active")}
            className={`${styles.tab} ${
              tabType === "active" ? styles.tabActive : ""
            }`}>
            {t("lbl_active_contracts")}
          </p>
          <p
            onClick={(e) => handleTabChange(e, "finished")}
            className={`${styles.tab} ${
              tabType === "finished" ? styles.tabActive : ""
            }`}>
            {t("lbl_finished_contracts")}
          </p>
        </div>
        <div className={styles.actions}>
          <SearchInput
            dir="rtl"
            placeholder={t("lbl_search_by_contract_id_or_title")}
            value={searchValue}
            style={{ width: "204px", height: "40px", borderRadius: "10px" }}
            handleChange={(event) => {
              setSearchValue(event.target.value);
            }}
          />

          <div className={styles.createButton}>
            {(authorities?.includes("CLIENT_ADMIN_CREATE_CONTRACT") ||
              authorities?.includes("CLIENT_ADMIN") ||
              authorities?.includes("ORGANIZATION_OWNER")) && (
              <PrimaryButton
                onClick={() => {
                  navigate(
                    ROUTES.CREATE_COMPLEX_CONTRACT.replace(
                      ":complexId?/",
                      ""
                    ).replace(":milestoneId?", "")
                  );
                }}>
                {t("lbl_create_new_project")}
              </PrimaryButton>
            )}
          </div>
        </div>
      </div>
      {contracts?.length === 0 ? (
        <>
          {!loading && (
            <div className={styles.emptyWrapper}>
              <div className={styles.emptyContent}>
                <img
                  src="/assets/images/svg/emptyState.svg"
                  alt={t("lbl_no_transactions")}
                  className={styles.emptyIcon}
                />
                <div className={styles.emptyText}>
                  <h5 className={styles.emptyTitle}>
                    {t("lbl_no_projects_yet")}
                  </h5>
                  <p className={styles.emptySubtitle}>
                    {t("lbl_create_new_project_now")}
                  </p>
                </div>
                {(authorities?.includes("CLIENT_ADMIN_CREATE_CONTRACT") ||
                  authorities?.includes("CLIENT_ADMIN") ||
                  authorities?.includes("ORGANIZATION_OWNER")) && (
                  <p
                    onClick={() =>
                      navigate(
                        ROUTES.CREATE_COMPLEX_CONTRACT.replace(
                          ":complexId?/",
                          ""
                        ).replace(":milestoneId?", "")
                      )
                    }
                    className={styles.emptyCta}>
                    {t("lbl_create_new_project")}
                  </p>
                )}
              </div>
            </div>
          )}
          {loading && (
            <div
              style={{
                width: "100%",
                height: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}>
              <Spinner />
            </div>
          )}
        </>
      ) : authorities?.includes("CLIENT_ADMIN_VIEW_CONTRACT") ||
        authorities?.includes("CLIENT_ADMIN") ||
        authorities?.includes("ORGANIZATION_OWNER") ? (
        <div className={styles.contractsContainer}>
          {contracts?.map((contract) => (
            <ContractsListItem
              key={(contract as { id: string })?.id}
              title={contract?.itemDetail?.title}
              description={contract?.itemDetail?.description}
              shortId={contract?.shortId}
              status={contract?.status}
              onClick={() =>
                navigate(
                  ROUTES.CONTRACT_DETAILS?.replace(":id", contract?.id),
                  { state: { from: location.pathname } }
                )
              }
              milestoneCount={contract?.activeChildrenCount}
              partiesCount={contract?.activeChildrenPartiesCount}
              itemPrice={contract?.activeChildrenTotalAmount}
            />
          ))}
        </div>
      ) : (
        <></>
      )}
      <div
        className={contracts?.length ? styles.paginatorContainer : styles.hide}>
        <Pagination
          countName={t("lbl_project")}
          pagesCount={pagesCount}
          length={totalCount}
          page={page}
          pageSize={pageSize}
          handlePaginationChange={handlePaginationChange}
        />
      </div>
    </div>
  );
};

export default Contracts;
